import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownAZ,
  ArrowUpAZ,
  ChevronRight,
  Copy,
  Eye,
  Folder,
  FolderPlus,
  FolderTree as FolderTreeIcon,
  HardDrive,
  ImageOff,
  LayoutGrid,
  List,
  Loader2,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Trash2,
  Upload,
  UploadCloud,
  X,
} from "lucide-react";
import FolderTree from "../../components/admin/media/FolderTree";
import MediaDetailPanel from "../../components/admin/media/MediaDetailPanel";
import useUploadQueue from "../../components/admin/media/useUploadQueue";
import { UploadQueuePanel, UploadStagingModal } from "../../components/admin/media/UploadPanel";
import { PROTECTED_ROOTS, TYPE_META, copyText, crumbsOf, fmtBytes, fmtDate, iconOfExt } from "../../components/admin/media/mediaUtils";
import { API, Button, Card, Drawer, EmptyState, Modal, Pagination, Skeleton, apiFetch, cx, useToast } from "./_shared";
import { usePermission } from "../../hooks/usePermission";

const LIMIT = 48;
const TYPE_CHIPS = [
  { value: "all", label: "Tất cả" },
  { value: "image", label: "Ảnh" },
  { value: "video", label: "Video" },
  { value: "doc", label: "Tài liệu" },
  { value: "other", label: "Khác" },
];
const SORTS = { date: "Ngày tải lên", name: "Tên", size: "Dung lượng", type: "Loại" };

function useMedia(query) {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return m;
}

/* ───────────── Thumbnail / thẻ tệp ───────────── */
function Thumb({ item, className }) {
  const [bad, setBad] = useState(false);
  const Icon = iconOfExt(item.ext);
  if (item.category === "image" && !bad) {
    return <img src={item.url} alt="" loading="lazy" onError={() => setBad(true)} className={cx("h-full w-full object-cover", className)} referrerPolicy="no-referrer" />;
  }
  return (
    <div className={cx("flex h-full w-full flex-col items-center justify-center gap-1 bg-admin-muted text-admin-textMuted", className)}>
      {bad ? <ImageOff size={22} strokeWidth={1.5} /> : <Icon size={24} strokeWidth={1.5} />}
      <span className="text-[10px] font-bold uppercase">{item.ext || "file"}</span>
    </div>
  );
}

function UsedBadge({ count }) {
  return count > 0 ? (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Dùng · {count}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">Chưa dùng</span>
  );
}

function RowMenu({ children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-admin-surface/95 text-admin-text shadow ring-1 ring-admin-border hover:bg-admin-muted"
        aria-label="Thao tác khác"
      >
        <MoreHorizontal size={16} />
      </button>
      {open ? (
        <div onClick={() => setOpen(false)} className="absolute right-0 top-9 z-30 w-48 overflow-hidden rounded-xl border border-admin-border bg-admin-surface py-1 shadow-xl">
          {children}
        </div>
      ) : null}
    </div>
  );
}
const MenuItem = ({ icon: Icon, danger, children, ...rest }) => (
  <button type="button" {...rest} className={cx("flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-admin-muted", danger ? "text-rose-600" : "text-admin-text")}>
    <Icon size={14} /> {children}
  </button>
);

function FileCard({ item, selected, active, onSelect, onOpen, onCopy, onRename, onDelete, canWrite, canDelete, selecting }) {
  return (
    <div
      className={cx(
        "group relative rounded-2xl border bg-admin-surface transition hover:shadow-md",
        active ? "border-admin-primary ring-2 ring-admin-primary/25" : selected ? "border-admin-primary/60 ring-1 ring-admin-primary/20" : "border-admin-border"
      )}
    >
      <button type="button" onClick={() => (selecting ? onSelect(item.key) : onOpen(item))} className="block w-full overflow-hidden rounded-t-2xl text-left" aria-label={`Xem ${item.name}`}>
        <div className="aspect-[4/3] w-full overflow-hidden bg-admin-muted">
          <Thumb item={item} className="transition duration-300 group-hover:scale-[1.03]" />
        </div>
      </button>
      <label className={cx("absolute left-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg bg-admin-surface/95 shadow ring-1 ring-admin-border transition", selected || selecting ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100")}>
        <input type="checkbox" checked={selected} onChange={() => onSelect(item.key)} className="h-4 w-4 accent-[rgb(var(--admin-primary))]" aria-label={`Chọn ${item.name}`} />
      </label>
      <div className={cx("absolute right-2 top-2 flex gap-1 transition", "opacity-0 group-hover:opacity-100 focus-within:opacity-100")}>
        <button type="button" onClick={() => onCopy(item.url)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-admin-surface/95 text-admin-text shadow ring-1 ring-admin-border hover:bg-admin-muted" title="Copy URL" aria-label="Copy URL">
          <Copy size={14} />
        </button>
        <RowMenu>
          <MenuItem icon={Eye} onClick={() => onOpen(item)}>Xem chi tiết</MenuItem>
          <MenuItem icon={Copy} onClick={() => onCopy(item.url)}>Copy URL</MenuItem>
          {canWrite ? <MenuItem icon={Pencil} onClick={() => onRename(item)}>Đổi tên</MenuItem> : null}
          {canDelete ? <MenuItem icon={Trash2} danger onClick={() => onDelete([item])}>Xoá</MenuItem> : null}
        </RowMenu>
      </div>
      <div className="space-y-1.5 p-3">
        <p className="truncate text-[13px] font-semibold text-admin-text" title={item.name}>
          {item.name}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-1 text-[11px] text-admin-textMuted">
          <span className="whitespace-nowrap">
            <span className="font-bold uppercase">{item.ext || "—"}</span> · {fmtBytes(item.size)}
          </span>
          <UsedBadge count={item.usedCount} />
        </div>
      </div>
    </div>
  );
}

function FolderCard({ folder, onOpen, onRename, onDelete, canWrite, canDelete }) {
  const locked = PROTECTED_ROOTS.has(folder.prefix);
  return (
    <div className="group relative flex items-center gap-3 rounded-2xl border border-admin-border bg-admin-surface p-3 transition hover:border-admin-primary/40 hover:shadow-md">
      <button type="button" onClick={() => onOpen(folder.prefix)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-500 ring-1 ring-amber-100">
          <Folder size={22} fill="currentColor" fillOpacity={0.2} />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-semibold text-admin-text">{folder.name}</span>
          <span className="block text-[11px] text-admin-textMuted">
            {folder.count} tệp · {fmtBytes(folder.size)}
          </span>
        </span>
      </button>
      {!locked && (canWrite || canDelete) ? (
        <RowMenu>
          {canWrite ? <MenuItem icon={Pencil} onClick={() => onRename(folder)}>Đổi tên</MenuItem> : null}
          {canDelete ? <MenuItem icon={Trash2} danger onClick={() => onDelete(folder)}>Xoá thư mục</MenuItem> : null}
        </RowMenu>
      ) : null}
    </div>
  );
}

function ListRow({ item, selected, active, onSelect, onOpen, onCopy, onRename, onDelete, canWrite, canDelete }) {
  return (
    <tr className={cx("group border-t border-admin-border transition hover:bg-admin-muted/50", active && "bg-admin-primaryLight/60")}>
      <td className="w-10 py-2 pl-4">
        <input type="checkbox" checked={selected} onChange={() => onSelect(item.key)} className="h-4 w-4 accent-[rgb(var(--admin-primary))]" aria-label={`Chọn ${item.name}`} />
      </td>
      <td className="py-2 pr-3">
        <button type="button" onClick={() => onOpen(item)} className="flex min-w-0 items-center gap-3 text-left">
          <span className="h-10 w-10 shrink-0 overflow-hidden rounded-lg ring-1 ring-admin-border">
            <Thumb item={item} />
          </span>
          <span className="min-w-0">
            <span className="block max-w-[320px] truncate text-[13px] font-semibold text-admin-text">{item.name}</span>
            <span className="block max-w-[320px] truncate font-mono text-[11px] text-admin-textMuted">{item.key}</span>
          </span>
        </button>
      </td>
      <td className="hidden px-3 py-2 md:table-cell">
        <span className={cx("rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1", (TYPE_META[item.category] || TYPE_META.other).tone)}>{(TYPE_META[item.category] || TYPE_META.other).label}</span>
      </td>
      <td className="px-3 py-2 text-[13px] text-admin-text">{fmtBytes(item.size)}</td>
      <td className="hidden px-3 py-2 text-[13px] text-admin-textMuted lg:table-cell">{fmtDate(item.lastModified)}</td>
      <td className="hidden px-3 py-2 sm:table-cell">
        <UsedBadge count={item.usedCount} />
      </td>
      <td className="py-2 pr-4 text-right">
        <div className="inline-flex gap-1">
          <button type="button" onClick={() => onCopy(item.url)} className="rounded-lg p-1.5 text-admin-textMuted hover:bg-admin-muted hover:text-admin-text" aria-label="Copy URL" title="Copy URL">
            <Copy size={15} />
          </button>
          {canWrite ? (
            <button type="button" onClick={() => onRename(item)} className="rounded-lg p-1.5 text-admin-textMuted hover:bg-admin-muted hover:text-admin-text" aria-label="Đổi tên" title="Đổi tên">
              <Pencil size={15} />
            </button>
          ) : null}
          {canDelete ? (
            <button type="button" onClick={() => onDelete([item])} className="rounded-lg p-1.5 text-admin-textMuted hover:bg-rose-50 hover:text-rose-600" aria-label="Xoá" title="Xoá">
              <Trash2 size={15} />
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

/* ───────────── Hộp thoại: xoá / đổi tên có cảnh báo tác động ───────────── */
function UsageList({ usages, total, truncated }) {
  const TYPE = { location: "Địa điểm", category: "Danh mục", setting: "Cài đặt", user: "Người dùng" };
  return (
    <>
      <ul className="mt-3 max-h-44 divide-y divide-admin-border overflow-y-auto rounded-xl border border-admin-border">
        {usages.map((u, i) => (
          <li key={`${u.id}-${u.path}-${i}`} className="flex items-center gap-2 px-3 py-2 text-[13px]">
            <span className="shrink-0 rounded bg-admin-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-admin-textMuted">{TYPE[u.type] || u.type}</span>
            <span className="min-w-0 flex-1 truncate font-medium text-admin-text">{u.name}</span>
            <span className="shrink-0 text-[11px] text-admin-textMuted">{u.field}</span>
          </li>
        ))}
      </ul>
      {truncated ? <p className="mt-1 text-[11px] text-admin-textMuted">Chỉ hiện {usages.length} / {total} vị trí đầu.</p> : null}
    </>
  );
}

function useUsage(body, enabled) {
  const [s, set] = useState({ loading: true, total: 0, usages: [], truncated: false });
  const key = JSON.stringify(body);
  useEffect(() => {
    if (!enabled) return undefined;
    let off = false;
    set({ loading: true, total: 0, usages: [], truncated: false });
    apiFetch(`${API}/media/usage`, { method: "POST", body: key })
      .then((r) => !off && set({ loading: false, total: r.total || 0, usages: r.usages || [], truncated: r.truncated }))
      .catch(() => !off && set({ loading: false, total: 0, usages: [], error: true }));
    return () => {
      off = true;
    };
  }, [key, enabled]);
  return s;
}

function DeleteDialog({ target, onClose, onConfirm }) {
  // target: { files: [item] } | { folder }
  const isFolder = Boolean(target.folder);
  const body = isFolder ? { prefix: target.folder.prefix } : { keys: target.files.map((f) => f.key) };
  const usage = useUsage(body, true);
  const [busy, setBusy] = useState(false);
  const inUse = usage.total > 0;
  const n = target.files?.length || 0;
  const name = isFolder ? target.folder.name : n === 1 ? target.files[0].name : `${n} tệp`;

  const go = async () => {
    setBusy(true);
    try {
      await onConfirm(inUse);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      title={
        <span className="flex items-center gap-2 text-base font-semibold text-admin-text">
          <Trash2 size={18} className="text-rose-600" /> {isFolder ? "Xoá thư mục" : "Xoá tệp"} “{name}”
        </span>
      }
      maxWidthClass="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>Huỷ</Button>
          <Button variant="danger" onClick={go} loading={busy} disabled={usage.loading}>
            {isFolder ? (inUse ? "Vẫn xoá vĩnh viễn thư mục" : "Xoá vĩnh viễn thư mục") : inUse ? "Vẫn chuyển vào thùng rác" : "Chuyển vào thùng rác"}
          </Button>
        </div>
      }
    >
      {isFolder ? (
        <p className="text-sm text-admin-text">
          Thư mục chứa <b>{target.folder.count} tệp</b> ({fmtBytes(target.folder.size)}). Hành động này <b>xoá vĩnh viễn</b> và không thể hoàn tác.
        </p>
      ) : (
        <p className="text-sm text-admin-text">Tệp sẽ được chuyển vào <b>Thùng rác</b>, bạn có thể khôi phục bất cứ lúc nào.</p>
      )}
      {usage.loading ? (
        <p className="mt-3 flex items-center gap-2 text-xs text-admin-textMuted"><Loader2 size={13} className="animate-spin" /> Đang kiểm tra nơi sử dụng…</p>
      ) : inUse ? (
        <>
          <div className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              Đang được dùng ở <b>{usage.total} vị trí</b>. Nếu xoá, các nơi bên dưới sẽ hiện ảnh lỗi cho tới khi bạn thay ảnh khác.
            </span>
          </div>
          <UsageList {...usage} />
        </>
      ) : (
        <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800">Chưa có nội dung nào đang dùng — an toàn để xoá.</p>
      )}
    </Modal>
  );
}

function RenameDialog({ target, onClose, onConfirm }) {
  const isFolder = target.type === "folder";
  const current = isFolder ? target.name : target.name;
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  const usage = useUsage(isFolder ? { prefix: target.prefix } : { keys: [target.key] }, true);
  const inUse = usage.total > 0;
  // Không cho "/" hoặc "\\" trong tên — nếu có sẽ vô tình di chuyển vào thư mục lồng nhau
  const clean = value.replace(/[/\\]/g, "").trim();
  const changed = clean && clean !== current;

  const go = async () => {
    setBusy(true);
    try {
      await onConfirm(clean, inUse);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      title={<span className="text-base font-semibold text-admin-text">Đổi tên {isFolder ? "thư mục" : "tệp"}</span>}
      maxWidthClass="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>Huỷ</Button>
          <Button variant="primary" onClick={go} loading={busy} disabled={!changed || usage.loading}>
            {inUse ? "Đổi tên & cập nhật liên kết" : "Đổi tên"}
          </Button>
        </div>
      }
    >
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && changed && !usage.loading && go()}
        className="h-10 w-full rounded-lg border border-admin-border bg-admin-surface px-3 text-sm outline-none focus:border-admin-primary focus:ring-2 focus:ring-admin-primaryRing"
      />
      {usage.loading ? (
        <p className="mt-3 flex items-center gap-2 text-xs text-admin-textMuted"><Loader2 size={13} className="animate-spin" /> Đang kiểm tra nơi sử dụng…</p>
      ) : inUse ? (
        <>
          <div className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              Đường dẫn cũ đang được dùng ở <b>{usage.total} vị trí</b>. Hệ thống sẽ tự cập nhật lại liên kết ở các nơi dưới đây để không bị hỏng. Link đã chia sẻ ra ngoài sẽ không còn hiệu lực.
            </span>
          </div>
          <UsageList {...usage} />
        </>
      ) : (
        <p className="mt-3 text-xs text-admin-textMuted">Chưa có nội dung nào dùng {isFolder ? "thư mục" : "tệp"} này. Link đã chia sẻ ra ngoài sẽ không còn hiệu lực sau khi đổi tên.</p>
      )}
    </Modal>
  );
}

function SimpleInputModal({ title, label, placeholder, confirmLabel, onClose, onConfirm }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!v.trim()) return;
    setBusy(true);
    try {
      await onConfirm(v.trim());
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={<span className="text-base font-semibold text-admin-text">{title}</span>} maxWidthClass="max-w-md" footer={<div className="flex justify-end gap-2"><Button onClick={onClose}>Huỷ</Button><Button variant="primary" onClick={go} loading={busy} disabled={!v.trim()}>{confirmLabel}</Button></div>}>
      <label className="mb-1.5 block text-xs font-semibold text-admin-textMuted">{label}</label>
      <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} placeholder={placeholder} className="h-10 w-full rounded-lg border border-admin-border bg-admin-surface px-3 text-sm outline-none focus:border-admin-primary focus:ring-2 focus:ring-admin-primaryRing" />
    </Modal>
  );
}

/* ───────────── Thùng rác ───────────── */
function TrashView({ canWrite, canDelete, toast, onChanged }) {
  const [state, setState] = useState({ loading: true, items: [], totalSize: 0, error: "" });
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null); // {keys} | {all:true}

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await apiFetch(`${API}/media/trash`);
      setState({ loading: false, items: r.items || [], totalSize: r.totalSize || 0, error: "" });
      setSel(new Set());
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e.message }));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = (k) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const keys = sel.size ? [...sel] : [];

  const restore = async (ks) => {
    setBusy(true);
    try {
      const r = await apiFetch(`${API}/media/trash/restore`, { method: "POST", body: JSON.stringify({ keys: ks }) });
      toast(`Đã khôi phục ${r.ok} tệp`, "success");
      await load();
      onChanged();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const purge = async (body) => {
    setBusy(true);
    try {
      const r = await apiFetch(`${API}/media/trash`, { method: "DELETE", body: JSON.stringify(body) });
      toast(`Đã xoá vĩnh viễn ${r.deleted} tệp`, "success");
      setConfirmPurge(null);
      await load();
      onChanged();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-admin-border px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-admin-text">Thùng rác</h2>
          <p className="text-xs text-admin-textMuted">{state.items.length} tệp · {fmtBytes(state.totalSize)} · khôi phục lại bất cứ lúc nào hoặc xoá vĩnh viễn.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {keys.length && canWrite ? <Button size="sm" icon={RotateCcw} onClick={() => restore(keys)} loading={busy}>Khôi phục ({keys.length})</Button> : null}
          {keys.length && canDelete ? <Button size="sm" variant="danger" icon={Trash2} onClick={() => setConfirmPurge({ keys })}>Xoá vĩnh viễn ({keys.length})</Button> : null}
          {!keys.length && state.items.length && canDelete ? <Button size="sm" variant="secondary" icon={Trash2} onClick={() => setConfirmPurge({ all: true })}>Dọn sạch thùng rác</Button> : null}
        </div>
      </div>
      {state.loading ? (
        <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : state.error ? (
        <EmptyState icon={AlertTriangle} title="Không tải được thùng rác" description={state.error} action={<Button size="sm" icon={RefreshCw} onClick={load}>Thử lại</Button>} />
      ) : !state.items.length ? (
        <EmptyState icon={Trash2} title="Thùng rác trống" description="Tệp bạn xoá sẽ nằm ở đây để có thể khôi phục." />
      ) : (
        <ul className="divide-y divide-admin-border">
          {state.items.map((it) => (
            <li key={it.key} className="flex items-center gap-3 px-5 py-2.5">
              <input type="checkbox" checked={sel.has(it.key)} onChange={() => toggle(it.key)} className="h-4 w-4 accent-[rgb(var(--admin-primary))]" aria-label={`Chọn ${it.name}`} />
              <span className="h-10 w-10 shrink-0 overflow-hidden rounded-lg ring-1 ring-admin-border"><Thumb item={it} /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-admin-text">{it.name}</span>
                <span className="block truncate font-mono text-[11px] text-admin-textMuted">từ /{it.originalKey}</span>
              </span>
              <span className="hidden text-xs text-admin-textMuted sm:block">{fmtBytes(it.size)}</span>
              <span className="hidden text-xs text-admin-textMuted md:block">Xoá {fmtDate(it.deletedAt, true)}</span>
              {canWrite ? <Button size="sm" icon={RotateCcw} onClick={() => restore([it.key])} disabled={busy}>Khôi phục</Button> : null}
            </li>
          ))}
        </ul>
      )}
      {confirmPurge ? (
        <Modal open onClose={() => setConfirmPurge(null)} title={<span className="text-base font-semibold text-admin-text">Xoá vĩnh viễn?</span>} maxWidthClass="max-w-md" footer={<div className="flex justify-end gap-2"><Button onClick={() => setConfirmPurge(null)}>Huỷ</Button><Button variant="danger" loading={busy} onClick={() => purge(confirmPurge)}>Xoá vĩnh viễn</Button></div>}>
          <p className="text-sm text-admin-text">
            {confirmPurge.all ? `Toàn bộ ${state.items.length} tệp trong thùng rác` : `${confirmPurge.keys.length} tệp đã chọn`} sẽ bị xoá vĩnh viễn khỏi S3 và <b>không thể khôi phục</b>.
          </p>
        </Modal>
      ) : null}
    </Card>
  );
}

/* ───────────── Trang chính ───────────── */
export default function AdminMedia() {
  const { push: toast, ToastPortal } = useToast();
  const { can } = usePermission();
  const canWrite = can("media:write");
  const canDelete = can("media:delete");
  const [sp, setSp] = useSearchParams();

  const prefix = sp.get("folder") || "";
  const inTrash = sp.get("trash") === "1";
  const type = sp.get("type") || "all";
  const used = sp.get("used") || "all";
  const sort = sp.get("sort") || "date";
  const order = sp.get("order") || "desc";
  const page = Math.max(1, Number(sp.get("page")) || 1);
  const minMB = sp.get("minMB") || "";
  const maxMB = sp.get("maxMB") || "";
  const from = sp.get("from") || "";
  const to = sp.get("to") || "";
  const qParam = sp.get("q") || "";
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem("adminMedia.view") === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("adminMedia.view", view);
    } catch {
      /* bỏ qua */
    }
  }, [view]);

  const setParams = useCallback(
    (patch, { keepPage = false } = {}) => {
      setSp(
        (old) => {
          const n = new URLSearchParams(old);
          Object.entries(patch).forEach(([k, v]) => {
            if (v === "" || v == null || v === "all" || (k === "sort" && v === "date") || (k === "order" && v === "desc") || (k === "page" && v === 1) || (k === "trash" && !v)) n.delete(k);
            else n.set(k, String(v));
          });
          if (!keepPage && !("page" in patch)) n.delete("page");
          return n;
        },
        { replace: true }
      );
    },
    [setSp]
  );

  // Ô tìm kiếm: gõ tới đâu nhập tới đó, gửi lên server sau 300ms
  const [qInput, setQInput] = useState(qParam);
  useEffect(() => setQInput(qParam), [qParam]);
  useEffect(() => {
    if (qInput === qParam) return undefined;
    const t = setTimeout(() => setParams({ q: qInput.trim() }), 300);
    return () => clearTimeout(t);
  }, [qInput, qParam, setParams]);

  const [data, setData] = useState({ folders: [], items: [], total: 0, pages: 1, totalSize: 0, filtering: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const reqRef = useRef(0);
  const [detailTick, setDetailTick] = useState(0);

  const loadOverview = useCallback(async (fresh) => {
    try {
      setOverview(await apiFetch(`${API}/media/overview${fresh ? "?fresh=1" : ""}`));
    } catch {
      /* thống kê không bắt buộc */
    }
  }, []);

  const loadLibrary = useCallback(
    async (fresh = false) => {
      const id = ++reqRef.current;
      setLoading(true);
      setError("");
      try {
        const q = new URLSearchParams({ prefix, type, used, sort, order, page: String(page), limit: String(LIMIT) });
        if (qParam) q.set("q", qParam);
        if (minMB) q.set("minSize", String(Math.round(Number(minMB) * 1048576)));
        if (maxMB) q.set("maxSize", String(Math.round(Number(maxMB) * 1048576)));
        if (from) q.set("from", from);
        if (to) q.set("to", to);
        if (fresh) q.set("fresh", "1");
        const r = await apiFetch(`${API}/media/library?${q.toString()}`);
        if (id !== reqRef.current) return;
        setData(r);
      } catch (e) {
        if (id === reqRef.current) setError(e.message || "Không tải được thư viện");
      } finally {
        if (id === reqRef.current) setLoading(false);
      }
    },
    [prefix, type, used, sort, order, page, qParam, minMB, maxMB, from, to]
  );

  useEffect(() => {
    if (!inTrash) loadLibrary();
  }, [loadLibrary, inTrash]);
  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const refresh = useCallback(() => {
    setDetailTick((t) => t + 1);
    loadLibrary(true);
    loadOverview(true);
  }, [loadLibrary, loadOverview]);

  /* chọn / chi tiết */
  const [selected, setSelected] = useState(new Set());
  const [detailKey, setDetailKey] = useState("");
  useEffect(() => {
    setSelected(new Set());
  }, [prefix, type, used, qParam, page, inTrash, minMB, maxMB, from, to, sort, order]);
  const detail = useMemo(() => data.items.find((i) => i.key === detailKey) || null, [data.items, detailKey]);
  const toggleSelect = (k) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const allOnPage = data.items.length > 0 && data.items.every((i) => selected.has(i.key));
  const selectedItems = data.items.filter((i) => selected.has(i.key));

  /* thao tác */
  const copyUrl = async (url) => {
    const ok = await copyText(url);
    toast(ok ? "Đã copy URL" : "Không copy được, hãy copy thủ công", ok ? "success" : "error");
  };
  const copyMany = async () => toast((await copyText(selectedItems.map((i) => i.url).join("\n"))) ? `Đã copy ${selectedItems.length} URL` : "Không copy được", "success");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);

  const confirmDelete = async (force) => {
    const t = deleteTarget;
    try {
      if (t.folder) {
        await apiFetch(`${API}/media/folder`, { method: "DELETE", body: JSON.stringify({ prefix: t.folder.prefix, ...(force ? { force: true } : {}) }) });
        toast("Đã xoá thư mục", "success");
      } else {
        const r = await apiFetch(`${API}/media/trash`, { method: "POST", body: JSON.stringify({ keys: t.files.map((f) => f.key), ...(force ? { force: true } : {}) }) });
        toast(r.failed?.length ? `Đã chuyển ${r.ok} tệp, ${r.failed.length} tệp lỗi` : `Đã chuyển ${r.ok} tệp vào thùng rác`, r.failed?.length ? "error" : "success");
        if (detailKey && t.files.some((f) => f.key === detailKey)) setDetailKey("");
        setSelected(new Set());
      }
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const confirmRename = async (name, inUse) => {
    const t = renameTarget;
    try {
      if (t.type === "folder") {
        const parent = t.prefix.split("/").slice(0, -2).join("/");
        const toPrefix = `${parent ? `${parent}/` : ""}${name}/`;
        await apiFetch(`${API}/media/folder/rename`, { method: "POST", body: JSON.stringify({ fromPrefix: t.prefix, toPrefix, ...(inUse ? { updateReferences: true } : {}) }) });
      } else {
        const parts = t.key.split("/");
        parts[parts.length - 1] = name;
        await apiFetch(`${API}/media/move`, { method: "POST", body: JSON.stringify({ fromKey: t.key, toKey: parts.join("/"), ...(inUse ? { updateReferences: true } : {}) }) });
        setDetailKey("");
      }
      toast("Đã đổi tên", "success");
      setRenameTarget(null);
      refresh();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const createFolder = async (name) => {
    try {
      await apiFetch(`${API}/media/folder`, { method: "POST", body: JSON.stringify({ prefix: `${prefix}${name.replace(/\//g, "-")}/` }) });
      toast("Đã tạo thư mục", "success");
      setNewFolderOpen(false);
      refresh();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  /* tải lên */
  const fileInputRef = useRef(null);
  const [staged, setStaged] = useState([]);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const uploader = useUploadQueue({
    onFinished: (n) => {
      toast(`Đã tải lên ${n} tệp`, "success");
      refresh();
    },
  });
  const stage = (files) => {
    if (!canWrite) return toast("Bạn không có quyền tải lên", "error");
    const list = Array.from(files || []).filter((f) => f && f.size >= 0);
    if (list.length) setStaged((s) => [...s, ...list]);
  };

  const filtersActive = [type !== "all", used !== "all", minMB, maxMB, from, to].filter(Boolean).length;
  const anyFilter = filtersActive > 0 || Boolean(qParam);
  const resetAll = () => {
    setQInput("");
    setParams({ q: "", type: "", used: "", minMB: "", maxMB: "", from: "", to: "", sort: "", order: "" });
  };

  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  useEffect(() => {
    if (!filterOpen) return undefined;
    const h = (e) => filterRef.current && !filterRef.current.contains(e.target) && setFilterOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [filterOpen]);

  const isXl = useMedia("(min-width: 1280px)");
  const isLg = useMedia("(min-width: 1024px)");
  const [treeOpen, setTreeOpen] = useState(false);

  const crumbs = crumbsOf(prefix);
  const openFolder = (p) => {
    setTreeOpen(false);
    setSp(
      (old) => {
        const n = new URLSearchParams(old);
        n.delete("trash");
        n.delete("page");
        p ? n.set("folder", p) : n.delete("folder");
        return n;
      },
      { replace: true }
    );
  };

  const tree = (
    <FolderTree
      tree={overview?.tree}
      current={prefix}
      onOpen={openFolder}
      total={overview?.files}
      trashCount={overview?.trashCount}
      inTrash={inTrash}
      onOpenTrash={() => {
        setTreeOpen(false);
        setParams({ trash: "1", folder: "" });
      }}
      totalSize={overview?.totalSize}
    />
  );

  const selecting = selected.size > 0;
  const cardProps = { onSelect: toggleSelect, onOpen: (i) => setDetailKey(i.key), onCopy: copyUrl, onRename: (i) => setRenameTarget({ type: "file", ...i }), onDelete: (files) => setDeleteTarget({ files }), canWrite, canDelete };
  const empty = !loading && !error && !data.items.length && !data.folders.length;

  return (
    <div
      className="relative pb-10"
      onDragEnter={(e) => {
        if (!e.dataTransfer?.types?.includes("Files") || inTrash) return;
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => e.dataTransfer?.types?.includes("Files") && e.preventDefault()}
      onDragLeave={() => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (!dragDepth.current) setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (!inTrash) stage(e.dataTransfer.files);
      }}
    >
      {/* ── Header ── */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-admin-text">Thư viện media</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-admin-textMuted">
            <span className="inline-flex items-center gap-1.5"><HardDrive size={14} /> {overview ? `${overview.files} tệp · ${fmtBytes(overview.totalSize)}` : "Đang tính…"}</span>
            {overview ? (
              <>
                <span>{overview.byType.image} ảnh</span>
                <span>{overview.byType.video} video</span>
                <span>{overview.byType.doc} tài liệu</span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isLg ? <Button icon={FolderTreeIcon} onClick={() => setTreeOpen(true)}>Thư mục</Button> : null}
          <Button icon={RefreshCw} onClick={refresh} aria-label="Làm mới">
            <span className="hidden sm:inline">Làm mới</span>
          </Button>
          {canWrite && !inTrash ? <Button icon={FolderPlus} onClick={() => setNewFolderOpen(true)}>Thư mục mới</Button> : null}
          {canWrite && !inTrash ? <Button variant="primary" icon={Upload} onClick={() => fileInputRef.current?.click()}>Tải lên</Button> : null}
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => { stage(e.target.files); e.target.value = ""; }} />
        </div>
      </div>

      <div className={cx("grid gap-5", isLg ? (detail && isXl ? "grid-cols-[230px_minmax(0,1fr)_360px]" : "grid-cols-[230px_minmax(0,1fr)]") : "grid-cols-1")}>
        {isLg ? (
          <Card className="h-fit max-h-[calc(100vh-7rem)] overflow-y-auto p-3 lg:sticky lg:top-20">
            <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted">Thư mục</p>
            {tree}
          </Card>
        ) : null}

        <div className="min-w-0 space-y-4">
          {inTrash ? (
            <TrashView canWrite={canWrite} canDelete={canDelete} toast={toast} onChanged={() => loadOverview(true)} />
          ) : (
            <>
              {/* ── Toolbar ── */}
              <Card className="p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative min-w-[200px] flex-1">
                    <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-admin-textMuted" />
                    <input
                      value={qInput}
                      onChange={(e) => setQInput(e.target.value)}
                      placeholder="Tìm theo tên, thư mục trên toàn thư viện…"
                      className="h-10 w-full rounded-lg border border-admin-border bg-admin-surface pl-9 pr-8 text-sm outline-none focus:border-admin-primary focus:ring-2 focus:ring-admin-primaryRing"
                    />
                    {qInput ? (
                      <button type="button" onClick={() => setQInput("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-admin-textMuted hover:text-admin-text" aria-label="Xoá tìm kiếm">
                        <X size={14} />
                      </button>
                    ) : null}
                  </div>

                  <div className="relative" ref={filterRef}>
                    <Button icon={SlidersHorizontal} onClick={() => setFilterOpen((o) => !o)} className={cx(filtersActive && "border-admin-primary text-admin-primary")}>
                      Bộ lọc{filtersActive ? ` (${filtersActive})` : ""}
                    </Button>
                    {filterOpen ? (
                      <div className="fixed inset-x-3 top-28 z-40 max-h-[70vh] overflow-y-auto rounded-2xl border border-admin-border bg-admin-surface p-4 shadow-2xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-12 sm:w-[340px]">
                        <div className="space-y-4">
                          <div>
                            <p className="mb-1.5 text-xs font-semibold text-admin-textMuted">Loại tệp</p>
                            <div className="flex flex-wrap gap-1.5">
                              {TYPE_CHIPS.map((c) => (
                                <button key={c.value} type="button" onClick={() => setParams({ type: c.value })} className={cx("rounded-full px-3 py-1 text-xs font-semibold ring-1 transition", type === c.value ? "bg-admin-primary text-white ring-admin-primary" : "bg-admin-surface text-admin-text ring-admin-border hover:bg-admin-muted")}>
                                  {c.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="mb-1.5 text-xs font-semibold text-admin-textMuted">Sử dụng</p>
                            <div className="flex gap-1.5">
                              {[["all", "Tất cả"], ["used", "Đang dùng"], ["unused", "Chưa dùng"]].map(([v, l]) => (
                                <button key={v} type="button" onClick={() => setParams({ used: v })} className={cx("rounded-full px-3 py-1 text-xs font-semibold ring-1 transition", used === v ? "bg-admin-primary text-white ring-admin-primary" : "bg-admin-surface text-admin-text ring-admin-border hover:bg-admin-muted")}>
                                  {l}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="mb-1.5 text-xs font-semibold text-admin-textMuted">Dung lượng (MB)</p>
                            <div className="flex items-center gap-2">
                              <input type="number" min="0" step="0.1" value={minMB} onChange={(e) => setParams({ minMB: e.target.value })} placeholder="Từ" className="h-9 w-full rounded-lg border border-admin-border bg-admin-surface px-2 text-sm outline-none focus:border-admin-primary" />
                              <span className="text-admin-textMuted">–</span>
                              <input type="number" min="0" step="0.1" value={maxMB} onChange={(e) => setParams({ maxMB: e.target.value })} placeholder="Đến" className="h-9 w-full rounded-lg border border-admin-border bg-admin-surface px-2 text-sm outline-none focus:border-admin-primary" />
                            </div>
                          </div>
                          <div>
                            <p className="mb-1.5 text-xs font-semibold text-admin-textMuted">Ngày tải lên</p>
                            <div className="flex items-center gap-2">
                              <input type="date" value={from} onChange={(e) => setParams({ from: e.target.value })} className="h-9 w-full rounded-lg border border-admin-border bg-admin-surface px-2 text-sm outline-none focus:border-admin-primary" />
                              <span className="text-admin-textMuted">–</span>
                              <input type="date" value={to} onChange={(e) => setParams({ to: e.target.value })} className="h-9 w-full rounded-lg border border-admin-border bg-admin-surface px-2 text-sm outline-none focus:border-admin-primary" />
                            </div>
                          </div>
                          <div className="flex justify-between border-t border-admin-border pt-3">
                            <Button size="sm" variant="ghost" onClick={resetAll}>Đặt lại bộ lọc</Button>
                            <Button size="sm" variant="primary" onClick={() => setFilterOpen(false)}>Xong</Button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-1 rounded-lg border border-admin-border bg-admin-surface px-1">
                    <select value={sort} onChange={(e) => setParams({ sort: e.target.value })} className="h-9 bg-transparent pl-2 pr-1 text-sm font-medium text-admin-text outline-none" aria-label="Sắp xếp theo">
                      {Object.entries(SORTS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button type="button" onClick={() => setParams({ order: order === "asc" ? "desc" : "asc" })} className="flex h-8 w-8 items-center justify-center rounded-md text-admin-textMuted hover:bg-admin-muted hover:text-admin-text" title={order === "asc" ? "Tăng dần" : "Giảm dần"} aria-label="Đổi chiều sắp xếp">
                      {order === "asc" ? <ArrowUpAZ size={16} /> : <ArrowDownAZ size={16} />}
                    </button>
                  </div>

                  <div className="flex rounded-lg bg-admin-muted p-0.5">
                    {[["grid", LayoutGrid, "Lưới"], ["list", List, "Danh sách"]].map(([v, Icon, l]) => (
                      <button key={v} type="button" onClick={() => setView(v)} aria-label={l} title={l} className={cx("flex h-9 w-9 items-center justify-center rounded-md transition", view === v ? "bg-admin-surface text-admin-primary shadow-sm" : "text-admin-textMuted hover:text-admin-text")}>
                        <Icon size={16} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* chip loại nhanh + tiêu chí đang áp dụng */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="flex gap-1 overflow-x-auto">
                    {TYPE_CHIPS.map((c) => (
                      <button key={c.value} type="button" onClick={() => setParams({ type: c.value })} className={cx("shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition", type === c.value ? "bg-admin-primaryLight text-admin-primary" : "text-admin-textMuted hover:bg-admin-muted")}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                  <span className="ml-auto text-xs text-admin-textMuted">
                    Sắp xếp: <b className="text-admin-text">{SORTS[sort]}</b> {order === "asc" ? "↑" : "↓"}
                  </span>
                  {anyFilter || sort !== "date" || order !== "desc" ? (
                    <button type="button" onClick={resetAll} className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline">
                      <RotateCcw size={12} /> Đặt lại
                    </button>
                  ) : null}
                </div>
              </Card>

              {/* ── Breadcrumb + kết quả ── */}
              <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <nav className="flex flex-wrap items-center gap-1 text-[13px]" aria-label="Đường dẫn">
                  {crumbs.map((c, i) => (
                    <React.Fragment key={c.prefix}>
                      {i > 0 ? <ChevronRight size={13} className="text-admin-textMuted" /> : null}
                      <button type="button" onClick={() => openFolder(c.prefix)} className={cx("rounded-md px-1.5 py-0.5 transition hover:bg-admin-muted", i === crumbs.length - 1 ? "font-semibold text-admin-text" : "text-admin-textMuted")}>
                        {c.label}
                      </button>
                    </React.Fragment>
                  ))}
                </nav>
                <p className="text-xs text-admin-textMuted">
                  {loading ? "Đang tải…" : data.filtering ? `${data.total} kết quả${prefix ? ` trong /${prefix}` : ""} · ${fmtBytes(data.totalSize)}` : `${data.folders.length} thư mục · ${data.total} tệp`}
                </p>
              </div>

              {/* ── Thanh thao tác hàng loạt ── */}
              {selecting ? (
                <div className="sticky top-16 z-20 flex flex-wrap items-center gap-3 rounded-2xl border border-admin-primary/30 bg-admin-primaryLight px-4 py-2.5 shadow-md">
                  <label className="flex items-center gap-2 text-sm font-semibold text-admin-primary">
                    <input type="checkbox" checked={allOnPage} onChange={() => setSelected(allOnPage ? new Set() : new Set(data.items.map((i) => i.key)))} className="h-4 w-4 accent-[rgb(var(--admin-primary))]" />
                    Đã chọn {selected.size} tệp
                  </label>
                  <div className="ml-auto flex gap-2">
                    <Button size="sm" icon={Copy} onClick={copyMany}>Copy URL</Button>
                    {canDelete ? <Button size="sm" variant="danger" icon={Trash2} onClick={() => setDeleteTarget({ files: selectedItems })}>Xoá</Button> : null}
                    <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Bỏ chọn</Button>
                  </div>
                </div>
              ) : data.items.length > 1 ? (
                <label className="flex w-fit cursor-pointer items-center gap-2 px-1 text-xs text-admin-textMuted">
                  <input type="checkbox" checked={false} onChange={() => setSelected(new Set(data.items.map((i) => i.key)))} className="h-3.5 w-3.5 accent-[rgb(var(--admin-primary))]" />
                  Chọn tất cả trong trang
                </label>
              ) : null}

              {/* ── Nội dung ── */}
              {loading && !data.items.length && !data.folders.length ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="overflow-hidden rounded-2xl border border-admin-border bg-admin-surface">
                      <Skeleton className="aspect-[4/3] w-full rounded-none" />
                      <div className="space-y-2 p-3"><Skeleton className="h-3 w-3/4" /><Skeleton className="h-3 w-1/2" /></div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <Card><EmptyState icon={AlertTriangle} title="Không tải được thư viện" description={error} action={<Button icon={RefreshCw} onClick={() => loadLibrary(true)}>Thử lại</Button>} /></Card>
              ) : empty ? (
                <Card>
                  {anyFilter ? (
                    <EmptyState icon={Search} title="Không tìm thấy tệp phù hợp" description="Thử đổi từ khoá hoặc bỏ bớt bộ lọc." action={<Button onClick={resetAll} icon={RotateCcw}>Đặt lại bộ lọc</Button>} />
                  ) : (
                    <EmptyState icon={UploadCloud} title="Thư mục này đang trống" description="Kéo thả tệp vào đây hoặc bấm Tải lên. Hỗ trợ chọn nhiều tệp cùng lúc." action={canWrite ? <Button variant="primary" icon={Upload} onClick={() => fileInputRef.current?.click()}>Tải lên</Button> : null} />
                  )}
                </Card>
              ) : (
                <div className={cx("space-y-5 transition-opacity", loading && "opacity-60")}>
                  {data.folders.length ? (
                    <div>
                      <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted">Thư mục</p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {data.folders.map((f) => (
                          <FolderCard key={f.prefix} folder={f} onOpen={openFolder} onRename={(x) => setRenameTarget({ type: "folder", prefix: x.prefix, name: x.name })} onDelete={(x) => setDeleteTarget({ folder: x })} canWrite={canWrite} canDelete={canDelete} />
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {data.items.length ? (
                    <div>
                      {data.folders.length ? <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted">Tệp</p> : null}
                      {view === "grid" ? (
                        <div className={cx("grid grid-cols-2 gap-4 sm:grid-cols-3", detail && isXl ? "xl:grid-cols-3" : "xl:grid-cols-4")}>
                          {data.items.map((it) => (
                            <FileCard key={it.key} item={it} selected={selected.has(it.key)} active={detailKey === it.key} selecting={selecting} {...cardProps} />
                          ))}
                        </div>
                      ) : (
                        <Card className="overflow-x-auto">
                          <table className="w-full min-w-[560px] text-left">
                            <thead>
                              <tr className="text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted">
                                <th className="py-2.5 pl-4" />
                                <th className="py-2.5">Tệp</th>
                                <th className="hidden px-3 py-2.5 md:table-cell">Loại</th>
                                <th className="px-3 py-2.5">Dung lượng</th>
                                <th className="hidden px-3 py-2.5 lg:table-cell">Ngày tải lên</th>
                                <th className="hidden px-3 py-2.5 sm:table-cell">Sử dụng</th>
                                <th className="py-2.5 pr-4" />
                              </tr>
                            </thead>
                            <tbody>
                              {data.items.map((it) => (
                                <ListRow key={it.key} item={it} selected={selected.has(it.key)} active={detailKey === it.key} {...cardProps} />
                              ))}
                            </tbody>
                          </table>
                        </Card>
                      )}
                    </div>
                  ) : null}
                  {(data.pages || 1) > 1 ? <Card><Pagination page={data.page || page} pages={data.pages} total={data.total} onChange={(p) => setParams({ page: p }, { keepPage: true })} /></Card> : null}
                </div>
              )}
            </>
          )}
        </div>

        {detail && isXl && !inTrash ? (
          <div className="sticky top-20 h-[calc(100vh-6rem)]">
            <MediaDetailPanel item={detail} refreshKey={detailTick} onClose={() => setDetailKey("")} onCopy={copyUrl} onRename={(i) => setRenameTarget({ type: "file", ...i })} onDelete={(files) => setDeleteTarget({ files })} canWrite={canWrite} canDelete={canDelete} />
          </div>
        ) : null}
      </div>

      {/* chi tiết dạng ngăn kéo cho màn hình nhỏ */}
      {detail && !isXl && !inTrash ? (
        <Drawer open onClose={() => setDetailKey("")} title="Chi tiết tệp" width="max-w-md">
          <div className="-m-5 h-[calc(100vh-4.5rem)]">
            <MediaDetailPanel item={detail} refreshKey={detailTick} onClose={() => setDetailKey("")} onCopy={copyUrl} onRename={(i) => setRenameTarget({ type: "file", ...i })} onDelete={(files) => setDeleteTarget({ files })} canWrite={canWrite} canDelete={canDelete} />
          </div>
        </Drawer>
      ) : null}

      <Drawer open={treeOpen && !isLg} onClose={() => setTreeOpen(false)} title="Thư mục" width="max-w-xs">
        {tree}
      </Drawer>

      {/* vùng thả tệp */}
      {dragging ? (
        <div className="pointer-events-none fixed inset-0 z-[130] flex items-center justify-center bg-admin-primary/10 backdrop-blur-[1px]">
          <div className="rounded-3xl border-2 border-dashed border-admin-primary bg-admin-surface px-10 py-8 text-center shadow-2xl">
            <UploadCloud size={40} className="mx-auto text-admin-primary" />
            <p className="mt-2 text-base font-semibold text-admin-text">Thả tệp để tải lên</p>
            <p className="text-xs text-admin-textMuted">vào /{prefix}</p>
          </div>
        </div>
      ) : null}

      <UploadStagingModal
        files={staged}
        prefix={prefix}
        onCancel={() => setStaged([])}
        onRemove={(i) => setStaged((s) => s.filter((_, x) => x !== i))}
        onConfirm={() => {
          uploader.enqueue(staged, prefix);
          setStaged([]);
        }}
      />
      <UploadQueuePanel jobs={uploader.jobs} onRetry={uploader.retry} onCancel={uploader.cancel} onClear={uploader.clearFinished} />

      {deleteTarget ? <DeleteDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} /> : null}
      {renameTarget ? <RenameDialog target={renameTarget} onClose={() => setRenameTarget(null)} onConfirm={confirmRename} /> : null}
      {newFolderOpen ? <SimpleInputModal title="Thư mục mới" label={`Tạo trong /${prefix}`} placeholder="Tên thư mục" confirmLabel="Tạo" onClose={() => setNewFolderOpen(false)} onConfirm={createFolder} /> : null}
      <ToastPortal />
    </div>
  );
}

